import { marketList } from './marketContants';

// Placeholder keys that exist in marketList as fallbacks and must never be
// treated as URL market prefixes.
const getValidMarketKeys = (): string[] =>
  Object.keys(marketList).filter(
    (key) => !!key && key !== 'undefined' && key !== 'null',
  );

/**
 * Returns the raw market segment (e.g. `'en'`, `'us'`, `'be-nl'`) found at the
 * start of `pathname`, or an empty string if none is present.
 *
 * Unlike `getUrlMarket`, this returns the segment exactly as it appears in the
 * URL — no aliasing is applied. This is what callers need when they want to
 * remove or replace the market prefix in the URL.
 */
export const getUrlMarketSegment = (pathname: string): string => {
  const lower = pathname.toLowerCase();
  for (const market of getValidMarketKeys()) {
    if (lower === `/${market}` || lower.startsWith(`/${market}/`)) {
      return market;
    }
  }
  return '';
};

/**
 * Resolves the URL market prefix to the internal market code used throughout
 * the app. `'en'` is treated as an alias for `'int'` (international); every
 * other prefix maps to itself. Returns `''` when no prefix is present.
 */
export const getUrlMarket = (pathname: string): string => {
  const segment = getUrlMarketSegment(pathname);
  if (!segment) return '';
  return segment === 'en' ? 'int' : segment;
};

/**
 * Strips whichever valid market prefix is present from the URL path, returning
 * the remainder (always starting with `/`). Falls back to the original
 * pathname when no known market prefix is detected.
 *
 * Detects the prefix from the URL itself (via `marketList`), so callers do NOT
 * need to know which market string was used — this handles aliases like
 * `/en/` (which the app maps to `int`) correctly.
 */
export const getPathWithoutMarket = (pathname: string): string => {
  const segment = getUrlMarketSegment(pathname);

  if (!segment) 
    return pathname;

  const rest = pathname.substring(segment.length + 1);

  if (rest === '') return '/';
    return rest.startsWith('/') ? rest : `/${rest}`;
};
